/**
 * The scene: map graph (specs/map-graph.md) + layout config → every card's
 * box, every connector's path with its label and knot point, every blocker,
 * every lane. One pure, deterministic function; the layers only draw what it
 * returns (specs/workshop-modules/impact_path.md → "Rendering: one scene").
 *
 * The map reads left to right as cause and time, top to bottom as parallel
 * strands; a thick orthogonal line is a causal claim, a thin curve behind the
 * cards an association. No strings: the renderer labels cards via t().
 */
import { SCENE_CONFIG, cardHeightFor } from './sceneConfig';
import { laneTrees, placeLane } from './layoutLanes';
import { assignColumns, FORWARD_TYPES } from './columns';
import { createRouter, isCausalType, curveBetween, labelAndKnot, orthD } from './routeEdges';
import { buildEdges } from './sceneEdges';
import { labelBySource } from './sceneLabels';
import { inkRoutes } from './sceneInk';

const NO_LANE = 'lane:none';
const isBlockerKind = (k) => k === 'blocker' || k === 'avoider';
const sortKey = (s) => String(s.attrs?.slug || s.attrs?.name || s.id);

export function buildScene(graph, config = {}) {
  const cfg = { ...SCENE_CONFIG, ...config };
  const step = cfg.cardWidth + cfg.columnGap;
  const colX = (c) => c * step;

  const nodes = (graph?.nodes || []).filter((n) => n.state !== 'rejected');
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  // New since the room last saw the map (map.seenAt set, entity not seen): the room
  // needs to find what the AI proposed since they looked.
  const seenAt = graph?.map?.attrs?.seenAt || null;
  const fresh = (entity) => Boolean(seenAt && entity.seen === false);
  const scenarios = [...(graph?.scenarios || [])].sort((a, b) => sortKey(a).localeCompare(sortKey(b), 'fi'));
  const scenarioIds = new Set(scenarios.map((s) => s.id));
  const laneOfId = (id) => (scenarioIds.has(id) ? id : (scenarioIds.has(nodeById.get(id)?.attrs.scenario) ? nodeById.get(id).attrs.scenario : NO_LANE));
  const isGoal = (id) => nodeById.get(id)?.attrs.kind === 'goal';
  // Shared targets (specs/tki-kuutio-flow.md): goals of no scenario, level 2, and the end
  // state above them, level 3 — their own columns after every lane's DONE. A question
  // card sits in the column before them, in its town's lane, beside what it asks about.
  const isTarget = (id) => {
    const n = nodeById.get(id);
    return Boolean(n && !n.attrs.scenario && n.attrs.kind === 'goal' && (n.attrs.level === 2 || n.attrs.level === 3));
  };
  const isQuestion = (id) => nodeById.get(id)?.attrs.kind === 'question';
  const offLane = (id) => isTarget(id) || isQuestion(id);

  // Edges between two things on the map. A goal has no structural parent, so
  // the start → goal edge older adapters wrote is not a claim and is not drawn.
  const dropped = [];
  const edges = (graph?.edges || []).filter((e) => {
    const { from, to } = e.attrs || {};
    if (e.state === 'rejected' || from === to) return false;
    if (!(nodeById.has(from) || scenarioIds.has(from)) || !nodeById.has(to)) { dropped.push({ id: e.id, why: 'end missing' }); return false; }
    if (scenarioIds.has(from) && isGoal(to)) { dropped.push({ id: e.id, why: 'start to goal' }); return false; }
    return true;
  });
  const edgeById = new Map(edges.map((e) => [e.id, e]));
  // A goal is an end — pinned to the goal column — unless the chain goes on
  // past it: a goal with a forward-typed successor keeps its place in the chain.
  // (A town goal leading on to a shared target is still its lane's end.)
  const goesOn = new Set(edges.filter((e) => FORWARD_TYPES.has(e.attrs.type) && nodeById.has(e.attrs.to) && !offLane(e.attrs.to)).map((e) => e.attrs.from));
  const isPinned = (id) => isGoal(id) && !goesOn.has(id);
  const primaryInto = new Map();
  edges.forEach((e) => { if (e.attrs.primary && !primaryInto.has(e.attrs.to)) primaryInto.set(e.attrs.to, e); });

  const milestoneOf = new Map();
  (graph?.marks || []).forEach((m) => {
    if (m.state !== 'rejected' && m.attrs?.type === 'milestone' && !milestoneOf.has(m.attrs.on)) {
      milestoneOf.set(m.attrs.on, { status: m.attrs.status || null, due: m.attrs.due || null });
    }
  });

  // Lanes: one per scenario, plus one for nodes whose scenario is unknown.
  const lanes = scenarios.map((s) => ({ id: s.id, scenario: s }));
  const laneNodes = nodes.filter((n) => !offLane(n.id));
  if (laneNodes.some((n) => laneOfId(n.id) === NO_LANE)) lanes.push({ id: NO_LANE, scenario: null });
  const nodesByLane = new Map(lanes.map((l) => [l.id, []]));
  laneNodes.forEach((n) => nodesByLane.get(laneOfId(n.id)).push(n));
  const targets = nodes.filter((n) => isTarget(n.id));
  // A question's lane: its own scenario, else the lane of the node it asks about; one about
  // a shared target with no town sits beside that target.
  const askedOf = new Map(edges.filter((e) => e.attrs.type === 'asks').map((e) => [e.attrs.from, e.attrs.to]));
  const questionLane = (id) => {
    const own = nodeById.get(id).attrs.scenario;
    if (scenarioIds.has(own)) return own;
    const asked = askedOf.get(id);
    return asked && !isTarget(asked) ? laneOfId(asked) : null;
  };
  const questionsByLane = new Map();
  const targetQuestions = new Map();
  nodes.filter((n) => isQuestion(n.id)).forEach((n) => {
    const lane = questionLane(n.id);
    const [map, key] = lane ? [questionsByLane, lane] : [targetQuestions, askedOf.get(n.id) || null];
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(n.id);
  });

  // A blocker stands on a causal tree edge of its own lane; one that does not
  // (an association is drawn behind the cards, with no run to stand on) is a
  // card like any other, reported in `unplaced`.
  const canHost = (e) => e?.attrs.primary && isCausalType(e.attrs.type);
  let placed = new Set(nodes.filter((n) => isBlockerKind(n.attrs.kind) && canHost(edgeById.get(n.attrs.on))).map((n) => n.id));
  let tree;
  for (let pass = 0; pass < 3; pass += 1) {
    tree = laneTrees(lanes, nodesByLane, primaryInto, isPinned, (id) => placed.has(id));
    const ok = new Set([...placed].filter((id) => {
      const e = edgeById.get(nodeById.get(id).attrs.on);
      return tree.parentOf.get(e.attrs.to) === e.attrs.from && primaryInto.get(e.attrs.to) === e;
    }));
    if (ok.size === placed.size) break;
    placed = ok;
  }
  const blockersOnEdge = new Map();
  nodes.forEach((n) => {
    if (!placed.has(n.id)) return;
    const k = n.attrs.on;
    if (!blockersOnEdge.has(k)) blockersOnEdge.set(k, []);
    blockersOnEdge.get(k).push(n.id);
  });
  const blockersOn = (from, to) => {
    const e = primaryInto.get(to);
    return e && e.attrs.from === from ? (blockersOnEdge.get(e.id) || []) : [];
  };

  const placedOn = new Map();
  blockersOnEdge.forEach((ids, edgeId) => ids.forEach((id, i) => placedOn.set(id, { host: edgeById.get(edgeId).attrs.to, offset: ids.length - i })));
  const loopClosers = new Set((graph?.loops || []).map((l) => {
    const c = typeof l.attrs?.closing === 'object' ? l.attrs.closing : edgeById.get(l.attrs?.closing)?.attrs;
    return c ? `${c.from}->${c.to}` : null;
  }).filter(Boolean));
  const { column, broken } = assignColumns({ lanes, tree, nodeById, blockersOn, edges, placedOn, loopClosers, isPinned });
  const lastMiddle = Math.max(1, ...column.values());
  const goalCol = lastMiddle + 1;
  const doneCol = goalCol + 1;
  const hasQuestions = questionsByLane.size > 0 || targetQuestions.size > 0;
  const questionCol = hasQuestions ? doneCol + 1 : null;
  const targetCol = (questionCol ?? doneCol) + 1;
  const endCol = targetCol + 1;

  const textOf = (id) => nodeById.get(id)?.attrs.text || '';
  const heightOf = (id) => cardHeightFor(textOf(id), cfg);
  const stackHeight = (ids) => ids.reduce((s, id) => s + heightOf(id), 0) + Math.max(0, ids.length - 1) * cfg.rowGap;
  const ownHeight = (id) => Math.max(heightOf(id), ...blockersOn(tree.parentOf.get(id), id).map(heightOf));

  const cards = [];
  const cardById = new Map();
  const addCard = (card) => {
    const w = cfg.cardWidth;
    const full = { ...card, w, x: colX(card.column) - w / 2, y: card.cy - card.h / 2, cx: colX(card.column) };
    cards.push(full);
    cardById.set(full.id, full);
    return full;
  };
  const nodeCard = (id, columnNo, cy, extra = {}) => {
    const n = nodeById.get(id);
    const k = n.attrs.kind;
    return addCard({
      id,
      kind: isBlockerKind(k) ? k : (k === 'goal' ? 'goal' : 'node'),
      nodeKind: k,
      text: n.attrs.text || '',
      valence: n.attrs.valence ?? null,
      source: n.claims?.[0]?.by?.type || null,
      state: n.state || 'accepted',
      fresh: fresh(n),
      milestone: milestoneOf.get(id) || null,
      scenario: n.attrs.scenario || null,
      lane: laneOfId(id),
      column: columnNo,
      cy,
      h: heightOf(id),
      ...extra,
    });
  };
  // Cards in one column, stacked and centred on cy
  const stack = (ids, columnNo, cy, extra = {}) => {
    let y = cy - stackHeight(ids) / 2;
    ids.forEach((id) => { const h = heightOf(id); nodeCard(id, columnNo, y + h / 2, extra); y += h + cfg.rowGap; });
  };

  const laneBoxes = [];
  let laneTop = 0;
  lanes.forEach((lane) => {
    const allGoals = (nodesByLane.get(lane.id) || []).filter((n) => isGoal(n.id)).map((n) => n.id);
    const goals = allGoals.filter(isPinned);
    const startText = lane.scenario?.attrs?.name || '';
    const startH = lane.scenario ? cardHeightFor(startText, cfg) : cfg.cardHeight;
    const goalsH = goals.reduce((s, id) => s + heightOf(id), 0) + Math.max(0, goals.length - 1) * cfg.rowGap;
    const questions = questionsByLane.get(lane.id) || [];
    const questionsH = stackHeight(questions);
    const centreHalf = Math.max(startH, goalsH, questionsH, cfg.cardHeight) / 2;
    const placedLane = placeLane(lane, laneTop, tree, ownHeight, centreHalf, cfg);
    const { ys, centreY } = placedLane;
    if (lane.scenario) {
      addCard({ id: lane.id, kind: 'start', text: startText, scenario: lane.id, lane: lane.id, column: 0, cy: centreY, h: startH, source: null, valence: null, milestone: null });
    }
    ys.forEach((cy, id) => nodeCard(id, column.get(id), cy, isGoal(id) ? { pinned: false } : {}));
    ys.forEach((cy, id) => {
      const on = blockersOn(tree.parentOf.get(id), id);
      on.forEach((b, i) => nodeCard(b, column.get(id) - on.length + i, cy, { placedOn: nodeById.get(b).attrs.on }));
    });
    let gy = centreY - goalsH / 2;
    goals.forEach((id) => { const h = heightOf(id); nodeCard(id, goalCol, gy + h / 2, { pinned: true }); gy += h + cfg.rowGap; });
    stack(questions, questionCol, centreY);
    if (lane.scenario) {
      addCard({ id: `done:${lane.id}`, kind: 'done', text: '', scenario: lane.id, lane: lane.id, column: doneCol, cy: centreY, h: cfg.cardHeight, goals: allGoals, source: null, valence: null, milestone: null });
    }
    laneBoxes.push({ id: lane.id, name: lane.scenario?.attrs?.name || '', slug: lane.scenario?.attrs?.slug || null, color: lane.scenario?.attrs?.color || null, top: placedLane.top, bottom: placedLane.bottom, centreY, left: colX(0) - cfg.cardWidth / 2, right: colX(doneCol) + cfg.cardWidth / 2 });
    laneTop = placedLane.bottom + cfg.laneGap;
  });

  // Targets: level 2 spread evenly over the lanes' full height, level 3 centred after them
  if (targets.length && laneBoxes.length) {
    const top = laneBoxes[0].top;
    const bottom = laneBoxes[laneBoxes.length - 1].bottom;
    const byOrder = (a, b) => String(a.attrs.target || a.attrs.text).localeCompare(String(b.attrs.target || b.attrs.text), 'fi');
    const level2 = targets.filter((n) => n.attrs.level === 2).sort(byOrder);
    const slot = (bottom - top) / Math.max(1, level2.length);
    level2.forEach((n, i) => nodeCard(n.id, targetCol, top + slot * (i + 0.5), { pinned: true, level: 2, question: n.attrs.question || null }));
    targets.filter((n) => n.attrs.level === 3).sort(byOrder).forEach((n, i, all) => {
      nodeCard(n.id, endCol, (top + bottom) / 2 + (i - (all.length - 1) / 2) * (cfg.cardHeight + cfg.rowGap) * 2, { pinned: true, level: 3 });
    });
    // Questions about a target and no town: beside it, below any card already in that column
    targetQuestions.forEach((ids, targetId) => {
      const t = cardById.get(targetId);
      if (!t || questionCol === null) return;
      let y = t.cy - stackHeight(ids) / 2;
      ids.forEach((id) => {
        const h = heightOf(id);
        const clash = () => cards.find((c) => c.column === questionCol && y < c.y + c.h + cfg.rowGap && y + h + cfg.rowGap > c.y);
        for (let c = clash(); c; c = clash()) y = c.y + c.h + cfg.rowGap;
        nodeCard(id, questionCol, y + h / 2);
        y += h + cfg.rowGap;
      });
    });
  }

  const map = graph?.map;
  if (map && laneBoxes.length) {
    const question = map.attrs?.question || map.attrs?.title || '';
    const cy = (laneBoxes[0].top + laneBoxes[laneBoxes.length - 1].bottom) / 2;
    addCard({ id: map.id, kind: 'mission', text: question, column: -1, cy, h: cardHeightFor(question, cfg), lane: null, scenario: null, source: null, valence: null, milestone: null });
  }

  const router = createRouter(cards, cfg, colX);
  const { edges: outEdges, bundles, unrouted, drawn } = buildEdges({
    edges, cardById, tree, primaryInto, blockersOnEdge, laneOfId, router, cfg,
    missionId: map?.id, laneIds: laneBoxes.map((l) => l.id),
  });
  // A goal reached partway along a chain still counts toward its lane's DONE:
  // a thin link behind the cards says so.
  cards.filter((c) => c.kind === 'goal' && !c.pinned && cardById.has(`done:${c.lane}`)).forEach((c) => {
    outEdges.push({ id: `done:${c.id}`, from: c.id, to: `done:${c.lane}`, type: null, kind: 'goal-done', causal: false, primary: false, tree: false, cross: false, behind: true, blockers: [], stubs: [], ...curveBetween(c, cardById.get(`done:${c.lane}`), cfg), label: null, forward: true, via: 'curve', clear: true });
  });

  const blockers = cards.filter((c) => c.placedOn).map((c) => ({ id: c.id, kind: c.kind, on: c.placedOn, edge: c.placedOn }));
  const unplaced = nodes.filter((n) => isBlockerKind(n.attrs.kind) && !placed.has(n.id)).map((n) => n.id);
  // A blocker with no run to stand on (its host edge dropped, as start → goal, or not
  // the tree's) still blocks what that edge led to: a connector from the card to the
  // host's target says so, or the card floats in its lane with nothing to tell why.
  // Not an edge of the graph: the renderer offers no decision or type change on it.
  const graphEdgeById = new Map((graph?.edges || []).map((e) => [e.id, e]));
  unplaced.forEach((id) => {
    const s = cardById.get(id);
    const t = cardById.get(graphEdgeById.get(nodeById.get(id).attrs.on)?.attrs?.to);
    if (!s || !t || s.id === t.id) return;
    const r = router.orthogonal(s, t, { preferBeforeTarget: t.pinned === true });
    const e = { id: `on:${id}`, from: id, to: t.id, type: 'blocks', kind: 'link', synthetic: true, causal: true, primary: false, tree: false, cross: false, behind: false, blockers: [], stubs: [], state: 'accepted', reason: null, points: r.points, path: orthD(r.points, cfg), ...labelAndKnot(r.points, cfg), via: r.via, clear: r.clear !== false, forward: t.column > s.column };
    outEdges.push(e);
    drawn.push(e.points);
  });
  // The type, once per source and type, where those connectors still run together.
  labelBySource(outEdges, cfg);
  // What is drawn in front: each route whole, shifted a pixel aside where it shares a run.
  const ink = inkRoutes(outEdges, cfg);
  outEdges.forEach((e) => { const d = ink.get(e.id); if (d) e.draw = d; });

  const loops = (graph?.loops || []).filter((l) => l.state !== 'rejected').map((l) => {
    const ids = (l.attrs?.nodes || []).filter((id) => cardById.has(id));
    const closing = l.attrs?.closing && typeof l.attrs.closing === 'object' ? l.attrs.closing : (edgeById.get(l.attrs?.closing)?.attrs || null);
    const a = cardById.get(closing?.from);
    const b = cardById.get(closing?.to);
    let path = null;
    if (a && b) {
      const p = { x: a.cx, y: a.y };
      const q = { x: b.cx, y: b.y };
      const lift = Math.max(60, Math.abs(p.x - q.x) * 0.25);
      path = `M ${p.x} ${p.y} Q ${(p.x + q.x) / 2} ${Math.min(p.y, q.y) - lift} ${q.x} ${q.y}`;
    }
    return { id: l.id, kind: l.attrs?.kind || null, direction: l.attrs?.direction || null, title: l.attrs?.title || null, nodes: ids, closing: closing ? { from: closing.from, to: closing.to } : null, path, behind: true };
  });

  const xs = [];
  const ysAll = [];
  cards.forEach((c) => { xs.push(c.x, c.x + c.w); ysAll.push(c.y, c.y + c.h); });
  outEdges.forEach((e) => e.points.forEach((p) => { xs.push(p.x); ysAll.push(p.y); }));
  const bounds = xs.length
    ? { minX: Math.min(...xs), minY: Math.min(...ysAll), maxX: Math.max(...xs), maxY: Math.max(...ysAll) }
    : { minX: 0, minY: 0, maxX: 0, maxY: 0 };

  // Causal links drawn right to left, and why: a feedback type pointing back
  // (that is a loop showing), an edge leaving a goal (pinned last), or a
  // forward cycle broken here. Listed so the map's makers see where its
  // direction breaks.
  const brokenIds = new Set(broken.map((b) => b.id));
  const backward = outEdges.filter((e) => e.causal && !e.forward && e.kind !== 'structural').map((e) => {
    const fromGoal = cardById.get(e.from)?.pinned === true;
    let reason = 'other';
    if (brokenIds.has(e.id)) reason = 'forward_cycle';
    else if (fromGoal) reason = 'from_goal';
    else if (!FORWARD_TYPES.has(e.type)) reason = 'feedback';
    return { id: e.id, fromGoal, reason };
  });

  return { cards, edges: outEdges, bundles, drawn, blockers, lanes: laneBoxes, loops, bounds, columns: { goal: goalCol, done: doneCol, question: questionCol, target: targetCol, end: endCol }, config: cfg, dropped, unrouted, unplaced, backward };
}

export default buildScene;
